import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase_config';
import PropTypes from 'prop-types';
import {
    Box, Button, Typography, Paper, Link, Alert, CircularProgress,
    Chip, Avatar, Divider, Tabs, Tab, Card
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
    Person, Email, Phone, AttachMoney, Language, School,
    Description, VideoLibrary, Badge
} from '@mui/icons-material';
import { updateTeacherStatus } from '../../firebase_config';
import RejectTeacherModal from '../../components/teachers/RejectTeacherModal';
import DashboardLayout from '../../components/layout/DashboardLayout';

// Helper component for Tab Panels
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`teacher-tabpanel-${index}`}
            aria-labelledby={`teacher-tab-${index}`}
            style={{ width: '100%', maxWidth: '100%' }}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: { xs: 2, md: 4 }, boxSizing: 'border-box', width: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

const TeacherDetail = () => {
    const { state } = useLocation();
    const { teacherId } = useParams();
    const navigate = useNavigate();

    const [teacher, setTeacher] = useState(state?.teacher || null);
    const [loading, setLoading] = useState(!teacher);
    const [modalOpen, setModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        if (!teacher) {
            const fetchTeacher = async () => {
                try {
                    const teacherDocRef = doc(db, 'tutor_profiles', teacherId);
                    const teacherDoc = await getDoc(teacherDocRef);
                    if (teacherDoc.exists()) {
                        setTeacher({ id: teacherDoc.id, ...teacherDoc.data() });
                    } else {
                        setError('Teacher not found.');
                    }
                } catch (err) {
                    setError('Failed to fetch teacher data.');
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            };
            fetchTeacher();
        }
    }, [teacherId, teacher]);

    const handleAction = async (status, rejectionReason = '') => {
        setIsSubmitting(true);
        setError('');
        setSuccess('');
        try {
            await updateTeacherStatus({ teacherId, status, rejectionReason });
            setSuccess(`Teacher has been ${status}.`);
            if (modalOpen) setModalOpen(false);
            setTimeout(() => navigate('/admin/pending-teachers'), 2000);
        } catch (err) {
            setError(`Failed to ${status} teacher.`);
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout title="Loading Application...">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    if (error && !teacher) {
        return (
            <DashboardLayout title="Error">
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
                <Button variant="outlined" onClick={() => navigate('/admin/pending-teachers')} sx={{ mt: 3, borderRadius: 2 }}>
                    Go Back
                </Button>
            </DashboardLayout>
        );
    }

    if (!teacher) return null;

    return (
        <DashboardLayout title="Review Application">
            {/* Forced full width, border-box, and hidden overflow to kill horizontal scrolling */}
            <Paper 
                elevation={0} 
                sx={{ 
                    width: '100%',
                    maxWidth: '100%',
                    mx: 'auto',
                    boxSizing: 'border-box',
                    overflowX: 'hidden', 
                    borderRadius: 3, 
                    border: '1px solid', 
                    borderColor: 'divider',
                }}
            >
                {/* --- Profile Header --- */}
                <Box sx={{ 
                    p: { xs: 2, sm: 3, md: 4 }, 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    alignItems: { xs: 'center', md: 'flex-start' }, 
                    gap: 3,
                    boxSizing: 'border-box'
                }}>
                    <Avatar 
                        src={teacher.photoURL} 
                        sx={{ 
                            width: { xs: 80, sm: 100, md: 120 }, 
                            height: { xs: 80, sm: 100, md: 120 }, 
                            border: '4px solid', 
                            borderColor: 'grey.100',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }} 
                    />
                    
                    <Box sx={{ 
                        flexGrow: 1, 
                        textAlign: { xs: 'center', md: 'left' }, 
                        width: '100%', 
                        minWidth: 0, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: { xs: 'center', md: 'flex-start' } 
                    }}>
                        <Typography 
                            variant="h4" 
                            component="h1" 
                            fontWeight="700" 
                            sx={{ lineHeight: 1.2, mb: 1, width: '100%' }}
                        >
                            {teacher.displayName}
                        </Typography>
                        
                        {/* Bulletproof email truncation so it cannot force the screen wide */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 2, 
                            width: '100%', 
                            justifyContent: { xs: 'center', md: 'flex-start' }
                        }}>
                            <Email fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }}/>
                            <Typography 
                                color="text.secondary" 
                                sx={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    maxWidth: { xs: '200px', sm: '100%' } // strict limit for small screens
                                }}
                            >
                                {teacher.email}
                            </Typography>
                        </Box>

                        <Chip 
                            icon={<AttachMoney />} 
                            label={`${teacher.hourlyRate ? `$${teacher.hourlyRate}` : 'N/A'} / hour`} 
                            color="primary" 
                            sx={{ fontWeight: 600, borderRadius: 2, px: 1 }} 
                        />
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1.5, 
                        mt: { xs: 1, md: 0 }, 
                        width: { xs: '100%', sm: 'auto' },
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <LoadingButton 
                            loading={isSubmitting} 
                            variant="contained" 
                            color="primary" 
                            onClick={() => handleAction('approved')} 
                            sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 600, textTransform: 'none' }}
                            disableElevation
                        >
                            Approve
                        </LoadingButton>
                        <LoadingButton 
                            loading={isSubmitting} 
                            variant="outlined" 
                            color="error" 
                            onClick={() => setModalOpen(true)} 
                            sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 600, textTransform: 'none' }}
                        >
                            Reject
                        </LoadingButton>
                    </Box>
                </Box>
                
                {success && <Alert severity="success" sx={{ mx: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>{success}</Alert>}
                {error && <Alert severity="error" sx={{ mx: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>{error}</Alert>}

                <Divider />

                {/* --- Tabs --- */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', width: '100%' }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange} 
                        aria-label="teacher details tabs" 
                        variant="scrollable" 
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 64 },
                        }}
                    >
                        <Tab icon={<Person />} iconPosition="start" label="Details" id="teacher-tab-0" />
                        <Tab icon={<Description />} iconPosition="start" label="Documents" id="teacher-tab-1" />
                        <Tab icon={<VideoLibrary />} iconPosition="start" label="Intro Video" id="teacher-tab-2" />
                    </Tabs>
                </Box>

                {/* --- Tab Content --- */}
                <TabPanel value={tabValue} index={0}>
                    {/* Replaced MUI Grid with native CSS Grid to prevent negative margin overflow */}
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                        gap: 2,
                        width: '100%'
                    }}>
                        <Card variant="outlined" sx={{ borderRadius: 3, gridColumn: { xs: '1 / -1' } }}>
                            <Box sx={{ p: 2.5 }}>
                                <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" letterSpacing={1} mb={1}>Biography</Typography>
                                <Typography variant="body1" color="text.primary" sx={{ whiteSpace: 'pre-line', overflowWrap: 'break-word' }}>{teacher.bio || 'N/A'}</Typography>
                            </Box>
                        </Card>
                        
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <Box sx={{ p: 2.5 }}>
                                <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" letterSpacing={1} mb={2}>Subjects</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {teacher.subjects?.map(s => <Chip key={s} label={s} icon={<School fontSize="small" />} variant="outlined" sx={{ borderRadius: 2 }} />) || <Typography variant="body2" color="text.secondary">N/A</Typography>}
                                </Box>
                            </Box>
                        </Card>
                        
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <Box sx={{ p: 2.5 }}>
                                <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" letterSpacing={1} mb={2}>Languages</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {teacher.languages?.map(l => <Chip key={l} label={l} icon={<Language fontSize="small" />} variant="outlined" sx={{ borderRadius: 2 }} />) || <Typography variant="body2" color="text.secondary">N/A</Typography>}
                                </Box>
                            </Box>
                        </Card>
                        
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <Box sx={{ p: 2.5 }}>
                                <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" letterSpacing={1} mb={1}>Gender</Typography>
                                <Typography variant="body1" color="text.primary" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>{teacher.gender || 'N/A'}</Typography>
                            </Box>
                        </Card>
                        
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <Box sx={{ p: 2.5 }}>
                                <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" letterSpacing={1} mb={1}>Phone</Typography>
                                <Typography variant="body1" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, overflowWrap: 'break-word' }}>
                                    <Phone fontSize="small" color="action" sx={{ flexShrink: 0 }} /> {teacher.phoneNumber || 'N/A'}
                                </Typography>
                            </Box>
                        </Card>
                    </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 600 }}>
                        <Button 
                            component={Link} 
                            href={teacher.idDocumentSignedUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            variant="outlined" 
                            startIcon={<Badge />} 
                            disabled={!teacher.idDocumentSignedUrl}
                            sx={{ borderRadius: 2, py: 1.5, justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                            View Official ID Document
                        </Button>
                        {teacher.qualificationsSignedUrls && teacher.qualificationsSignedUrls.length > 0
                            ? teacher.qualificationsSignedUrls.map((url, i) => (
                                <Button 
                                    key={i} 
                                    component={Link} 
                                    href={url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    variant="outlined" 
                                    startIcon={<Description />}
                                    sx={{ borderRadius: 2, py: 1.5, justifyContent: 'flex-start', textTransform: 'none' }}
                                >
                                    View Qualification Document {i + 1}
                                </Button>
                              ))
                            : <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
                                  <Typography color="text.secondary">No qualification documents uploaded.</Typography>
                              </Paper>
                        }
                    </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
                        {teacher.introVideoSignedUrl ? (
                            <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 3, border: '1px solid', borderColor: 'divider', width: '100%' }}>
                                <video controls style={{ display: 'block', width: '100%', maxWidth: '100%' }}>
                                    <source src={teacher.introVideoSignedUrl} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </Paper>
                        ) : (
                            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3, borderStyle: 'dashed' }}>
                                <VideoLibrary color="disabled" sx={{ fontSize: 48, mb: 2 }} />
                                <Typography variant="h6" color="text.primary" gutterBottom>No Video Available</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    The applicant did not provide an introduction video.
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                </TabPanel>
            </Paper>

            <RejectTeacherModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={(reason) => handleAction('rejected', reason)} />
        </DashboardLayout>
    );
};

export default TeacherDetail;